import { ComponentItem } from "designSystem/types/component-item-types";
import { ILocaleProvider } from "designSystem/locale";
import configPreferences from "csi/config/preferences";
import ExtendedPath from "csi/core/ExtendedPath";
import { ATTR_CURRENT_REVISION, ATTR_LATEST_REVISION, getClass, getDomainClass, getDomainClasses, TYPE_CLASS_CONFIGURATION, TYPE_SUBTYPE, TYPE_TABLE_REPORT } from "csi/reflection/reflection";
import ReflectionAttribute from "csi/reflection/ReflectionAttribute";
import ReflectionClass from "csi/reflection/ReflectionClass";
import ReflectionDomainClass from "csi/reflection/ReflectionDomainClass";
import { IReflectionDomainAttribute } from "csi/reflection/reflectionTypes";
import { stringCombine } from "csi/utility/stringUtility";
import _ from "lodash";
import { ATTR_PARENT, C8AttributeId, C8NodeType } from "src/c8-platform/type/node";

// TODO: remove relations if the path through them is omitted

// TODO: handle matrices

// TODO: hide paths?
// Make MasterContainer ref private
// Stop traversing if destination class is user?
// Document parent should be private or omitted

const ABSTRACT_TYPES_FOR_PICKER = [
	"DataSheet",
];

const NO_ALIAS_TYPES_FOR_PICKER = [
	// TBD
];

const OMIT_RELATIONS = {
	ModifiedBy: 1,
};

export class APAttribute {
	readonly name: string;
	readonly path: ExtendedPath;
	readonly ra: ReflectionAttribute;

	get id(): string {
		return this.ra.attributeId;
	}

	matches(text: string): boolean {
		return this.name.toLowerCase().includes(text);
	}

	constructor(ra: ReflectionAttribute, ep: ExtendedPath, name: string) {
		this.name = name;
		this.path = ep;
		this.ra = ra;
	}
}

export class APGroup {
	readonly attributes: APAttribute[];
	readonly derivedFrom: APGroup;
	readonly groupPath: ExtendedPath;
	readonly id: string;
	readonly loc: ILocaleProvider;

	get attributeIdSet(): Set<C8AttributeId> {
		return this.attributes.reduce((memo, a) => {
			memo.add(a.id);
			return memo;
		}, new Set<C8AttributeId>());
	}

	get depth(): number {
		return this.groupPath.depth;
	}

	get info(): string {
		const { derivedFrom, groupPath } = this;
		return derivedFrom ? `(inherited from ${derivedFrom.name})` : groupPath.toString();
	}

	get name(): string {
		const { loc, pathName, type } = this;
		return stringCombine(loc.type(type), pathName, " - ");
	}

	get pathName(): string {
		return configPreferences.getPathName(this.groupPath);
	}

	get reflectionClass(): ReflectionClass {
		return getClass(this.type);
	}

	get type(): C8NodeType {
		return this.groupPath.getEndType();
	}

	matches(text: string): boolean {
		return !text || this.attributes.some((a) => a.matches(text));
	}

	constructor(ep: ExtendedPath, attributes: APAttribute[], loc: ILocaleProvider, derivedFrom?: APGroup) {
		this.attributes = _.sortBy(attributes, "name");
		this.derivedFrom = derivedFrom;
		this.groupPath = ep;
		this.id = this.groupPath.toString();
		this.loc = loc;
	}
}

function _createDomainAttribute(dc: ReflectionDomainClass, da: IReflectionDomainAttribute, prefixPath: ExtendedPath, loc: ILocaleProvider): APAttribute {
	const ep = getDomainAttributeExtendedPath(dc, da, prefixPath);
	const name = loc.attributeResource(ep.getEndType(), da.rca.attributeId);
	return new APAttribute(da.rca, ep, name);
}

function _createReflectionAttribute(rc: ReflectionClass, rca: ReflectionAttribute, prefixPath: ExtendedPath, loc: ILocaleProvider): APAttribute {
	const name = loc.attributeResource(rc.type, rca.attributeId);
	return new APAttribute(rca, prefixPath, name);
}

function _createGroup(rc: ReflectionClass, groupPath: ExtendedPath, loc: ILocaleProvider, derivedFrom?: APGroup): APGroup | null {
	const dc = getDomainClass(rc.type);
	const attributes: APAttribute[] = [];

	if (dc) {
		const omitSet = derivedFrom?.attributeIdSet;
		const domainAttributes = dc.getDomainAttributes(true, omitSet);
		const domainRelations = dc.getDomainRelations(true, omitSet);

		// collect attributes on domain class
		domainAttributes.forEach((da) => {
			attributes.push(_createDomainAttribute(dc, da, groupPath, loc));
		});

		// collect relations on domain class
		domainRelations.forEach((dr) => {
			attributes.push(_createDomainAttribute(dc, dr, groupPath, loc));
		});
	} else {
		// handle just reflection class (such as DataSheet)
		rc.getAttributes(true, true, false, derivedFrom?.reflectionClass).forEach((ra) => {
			if (!ra.isHidden) {
				attributes.push(_createReflectionAttribute(rc, ra, groupPath, loc));
			}
		});
	}

	if (attributes.length) {
		return new APGroup(groupPath, attributes, loc, derivedFrom);
	}

	return null;
}

function _traversePath(groups: APGroup[], ep: ExtendedPath, remainingDepth: number, loc: ILocaleProvider): void {
	const endClass = ep.getEndClass();
	const group = _createGroup(endClass, ep, loc);

	if (!group) {
		return;
	}

	groups.push(group);

	if (endClass.abstract) {
		// process concrete classes of abstract class
		endClass.getConcreteClasses().forEach((rc) => {
			const concreteGroup = _createGroup(rc, ep.clone().setEndType(rc.type), loc, group);
			if (concreteGroup) {
				groups.push(concreteGroup);
			}
		});
	}

	// traverse deeper if necessary
	if (remainingDepth) {
		group.attributes.forEach(({ path, id, ra }) => {
			const { isRelation, isSingle } = ra;

			// only process singular relations (for now - matrices?)
			if (isRelation && isSingle) {
				const destinationClass = getValidRelationDestination(path.getEndClass(), ra, path.endRelationId);
				if (destinationClass) {
					const childPath = path.clone().addRelationStep(id, destinationClass.type);
					childPath.resolveToDomain();

					// do not traverse paths that are omitted
					if (!configPreferences.isPathHidden(childPath)) {
						_traversePath(groups, childPath, remainingDepth - 1, loc);
					}
				}
			}
		});
	}
}

/**
 * Calculate attribute groups to the given depth given a starting domain type (or could be abstract)
 */
export function calculateAttributeGroups(startType: string, depth: number, loc: ILocaleProvider): APGroup[] {
	const groups: APGroup[] = [];
	const ep = new ExtendedPath(startType);
	_traversePath(groups, ep, depth, loc);
	return _.sortBy(groups, "name");
}

export function getAttributePickerTypes(): C8NodeType[] {
	const types = getDomainClasses()
		.filter((dc) => isValidClassForAttributePicker(dc.primary))
		.map(({ type }) => type);

	return [...types, ...ABSTRACT_TYPES_FOR_PICKER];
}

export function getAttributePickerComponentItems(loc: ILocaleProvider): ComponentItem[] {
	const options = getAttributePickerTypes().map((type) => ({
		id: type,
		name: loc.type(type),
	}));

	return _.sortBy(options, "name");
}

/**
 * Return extended path to attribute, including the prefix path if provided
 */
export function getDomainAttributeExtendedPath(dc: ReflectionDomainClass, da: IReflectionDomainAttribute, prefixPath?: ExtendedPath): ExtendedPath {
	const { secondaryId } = da;

	if (secondaryId) {
		const secondaryClass = dc.getDomainAttributeClass(da.domainId);
		const secondaryPath = prefixPath?.clone() || ExtendedPath.create(dc.primary);
		return secondaryPath.addSecondaryRelationStep(secondaryId, secondaryClass.type);
	}

	return prefixPath || ExtendedPath.create(dc.primary);
}

/**
 * Return true if relation from given class points to a destination class that is valid.
 * @param previousRelationId - indicates relation that was traversed to get to this relation
 */
export function getValidRelationDestination(sourceClass: ReflectionClass, relation: ReflectionAttribute, previousRelationId?: C8AttributeId): ReflectionClass | null {
	const { attributeDisabled, attributeId, bidirectionalRelation, isHidden, isSingle, isViableMatrix } = relation;

	if (attributeDisabled || isHidden || OMIT_RELATIONS[attributeId]) {
		return null;
	}

	if (!isSingle && !isViableMatrix) {
		return null;
	}

	if (previousRelationId && (previousRelationId === bidirectionalRelation?.attributeId)) {
		return null;
	}

	// handle special case to not return to master when coming from it
	if ((attributeId === ATTR_PARENT) && ((previousRelationId === ATTR_CURRENT_REVISION) || (previousRelationId === ATTR_LATEST_REVISION))) {
		return null;
	}

	const rcDest = relation.getDestinationClass(sourceClass);
	if (!rcDest || !isValidClassForAttributePicker(rcDest)) {
		return null;
	}

	return rcDest;
}

export function isValidClassForAttributePicker(rc: ReflectionClass): boolean {
	const { alias, disabled, type } = rc;

	if (disabled) {
		return false;
	}

	// allow revision classes even though they are not Business Objects
	if (rc.isRevision()) {
		return true;
	}

	if (!alias || NO_ALIAS_TYPES_FOR_PICKER.includes(type)) {
		return false;
	}

	// disregard certain configuration classes
	if (rc.isDerivedFrom(TYPE_CLASS_CONFIGURATION) ||
		rc.isDerivedFrom(TYPE_SUBTYPE) ||
		rc.isDerivedFrom(TYPE_TABLE_REPORT)) {
		return false;
	}

	return true;
}
